import React, { useState } from "react";
import {
    Box,
    TextField,
    Button,
    Paper,
    Typography,
    IconButton,
    Tooltip,
    CircularProgress,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { ethers } from "ethers";
import { useWallet } from "../context/WagmiWalletContext";
import logger from "../../utils/logger";
import ContractCallHelper from "../../utils/contractCallHelper";

interface DelayHelperProps {
    contractAddress?: string;
    onDelayFetched?: (delay: string) => void;
}

/**
 * A component for fetching the minimum delay from a timelockController contract
 * and formatting it for use in the schedule function.
 */
const DelayHelper: React.FC<DelayHelperProps> = ({ contractAddress, onDelayFetched }) => {
    const { provider } = useWallet();
    const [delay, setDelay] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [copied, setCopied] = useState<boolean>(false);

    // Fetch the minimum delay from the contract
    const fetchMinDelay = async () => {
        if (!provider || !contractAddress) {
            setError("Provider or contract address not available");
            return;
        }

        setLoading(true);
        setError("");

        try {
            logger.log("Attempting to fetch min delay from contract:", {
                contractAddress,
                provider: !!provider
            });

            // First, let's verify the contract exists by checking if it has code
            const code = await provider.getCode(contractAddress);
            if (code === '0x') {
                throw new Error(`No contract found at address ${contractAddress}. The contract may not be deployed or the address is incorrect.`);
            }

            logger.log("Contract code found, proceeding with getMinDelay call");

            // Use the ContractCallHelper for robust contract calls with retry logic
            const delayInSeconds = await ContractCallHelper.getMinDelay(contractAddress, provider);

            logger.log("Min delay fetched successfully:", {
                delay: delayInSeconds,
                contractAddress: contractAddress
            });

            setDelay(delayInSeconds);

            // Call the callback if provided
            if (onDelayFetched) {
                onDelayFetched(delayInSeconds);
            }
        } catch (error) {
            logger.error("Error fetching min delay:", error);
            const errorMessage = error instanceof Error ? error.message : String(error);

            // Provide more helpful error messages
            let userFriendlyError = errorMessage;
            if (errorMessage.includes('missing revert data')) {
                userFriendlyError = `Contract call failed: The function 'getMinDelay()' may not exist on this contract, or the contract is not properly deployed. Please verify the contract address: ${contractAddress}`;
            } else if (errorMessage.includes('Block height mismatch')) {
                userFriendlyError = `Network sync issue detected. Please try refreshing the page or reconnecting your wallet. If the problem persists, the contract may not be deployed on the current network.`;
            } else if (errorMessage.includes('Internal JSON-RPC error')) {
                userFriendlyError = `RPC connection error. Please check your wallet connection and try again. If using a local network, ensure Anvil is running.`;
            } else if (errorMessage.includes('No contract found')) {
                userFriendlyError = errorMessage; // Already user-friendly
            }

            setError(userFriendlyError);
        } finally {
            setLoading(false);
        }
    };

    // Handle manual delay input
    const handleDelayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newDelay = event.target.value;
        setDelay(newDelay);

        // Clear any previous errors when user starts typing
        if (error) {
            setError("");
        }

        // Call the callback if provided
        if (onDelayFetched && newDelay) {
            onDelayFetched(newDelay);
        }
    };

    // Copy the delay to clipboard
    const handleCopyDelay = () => {
        if (delay) {
            navigator.clipboard.writeText(delay);
            setCopied(true);

            // Reset the copied state after 2 seconds
            setTimeout(() => {
                setCopied(false);
            }, 2000);
        }
    };

    return (
        <Paper elevation={3} sx={{ p: 2, borderRadius: 1, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
                Timelock Delay Helper
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Fetch the minimum delay required for the timelockController.schedule function.
                This is the minimum time (in seconds) that must pass before the scheduled operation can be executed.
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TextField
                    fullWidth
                    label="Minimum Delay (seconds)"
                    value={delay}
                    onChange={handleDelayChange}
                    placeholder="Enter delay in seconds (e.g., 900)"
                    InputProps={{
                        endAdornment: loading ? <CircularProgress size={20} /> : null,
                    }}
                    helperText={error || (delay ? "This is the minimum required delay in seconds" : "You can manually enter a delay or fetch it from the contract")}
                    error={!!error}
                />

                <Tooltip title="Copy to Clipboard">
                    <span>
                        <IconButton
                            onClick={handleCopyDelay}
                            sx={{ ml: 1 }}
                            color={copied ? "success" : "default"}
                            disabled={!delay}
                        >
                            <ContentCopyIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            <Button
                variant="contained"
                onClick={fetchMinDelay}
                startIcon={<AccessTimeIcon />}
                fullWidth
                disabled={loading || !contractAddress}
            >
                {loading ? "Fetching..." : "Get Minimum Delay"}
            </Button>

            {copied && (
                <Typography
                    variant="caption"
                    color="success.main"
                    sx={{ display: 'block', mt: 1, textAlign: 'center' }}
                >
                    Delay copied to clipboard!
                </Typography>
            )}
        </Paper>
    );
};

export default DelayHelper;
